import useSettingsPanel from '@Guidelines/Shell/AppShell/Hooks/useSettingsPanel';
import useVisibleNavigationMenu from '@Guidelines/Shell/AppShell/Hooks/useVisibleNavigationMenu';
import {
	SETTINGS_PANEL_ID,
	SETTINGS_TOGGLE_ID,
} from '@Guidelines/Shell/AppShell/Menu/NavigationMenu/Desktop/SettingsPanel';
import { theme } from '@Guidelines/Theme/theme';
import { SETTINGS_SECTION_IDS } from '@UI/Routing/navigationMenu';
import { css } from '@emotion/react';
import { Settings } from 'lucide-react';
import * as React from 'react';

const buttonStyle = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.5rem;
	height: 2.5rem;
	flex-shrink: 0;
	padding: 0;
	border: none;
	border-radius: ${theme.radii.pill};
	background-color: transparent;
	color: ${theme.colors.ink};
	cursor: pointer;
	transition:
		background-color ${theme.motion.duration},
		color ${theme.motion.duration};

	&:hover {
		background-color: ${theme.colors.surfaceMuted};
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: none;
	}
`;

const activeButtonStyle = css`
	background-color: ${theme.colors.accentSurface};
	color: ${theme.colors.accentDark};

	&:hover {
		background-color: ${theme.colors.accentSurface};
	}
`;

/**
 * Opens or closes the settings panel over the page; icon only, shown from `layout.compactBreakpoint` up and
 * only when a settings entry is accessible. Active while the panel is displayed; on a settings route the
 * panel is pinned, so the click does nothing.
 */
const SettingsToggleButton: React.FC = () => {
	const { mode, open, close } = useSettingsPanel();
	const sections = useVisibleNavigationMenu(SETTINGS_SECTION_IDS);

	const handleClick = React.useCallback(() => {
		if (mode === 'closed') {
			open();
		} else if (mode === 'overlay') {
			close();
		}
	}, [mode, open, close]);

	if (sections.length === 0) {
		return null;
	}

	return (
		<button
			id={SETTINGS_TOGGLE_ID}
			type="button"
			css={[buttonStyle, mode !== 'closed' ? activeButtonStyle : null]}
			onClick={handleClick}
			aria-label="Paramètres"
			aria-expanded={mode !== 'closed'}
			aria-controls={SETTINGS_PANEL_ID}
		>
			<Settings size={20} aria-hidden="true" />
		</button>
	);
};

export default SettingsToggleButton;
